import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { plainConfirmMessage, usePreviewConfirm } from "@/lib/confirmFlow";
import { formatCents, formatDate, mountainDay } from "@/lib/format";
import { centsToInput, toCents } from "@/lib/money";
import { gatewayName } from "@/lib/payments";
import { useIsAdmin } from "@/lib/session";
import { useToast } from "@/components/Toaster";
import { Facts, Problem, ServerSays } from "@/components/PreviewBits";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { Select } from "@/components/ui/select";

// Invoice and payment fixes from the order page (crm-billing ops: invoices_void, payments_record_manual,
// billing_payment_resolve). Each shows what will happen before it does anything.

const REFRESH = ["order", "orders", "payments", "billing-summary", "billing-detail", "subscriptions", "subscription", "billing-runs", "billing-run-payments"];
function useRefresh() {
  const qc = useQueryClient();
  return () => {
    for (const k of REFRESH) void qc.invalidateQueries({ queryKey: [k] });
  };
}

export interface FixableInvoice {
  id: number;
  number: string;
  status: string;
  amount_due_cents: number;
  amount_paid_cents: number;
  subscription_id?: number | null;
}

/** Void and "Record a check, cash or wire" on an invoice that hasn't been paid (both only for open invoices). */
export function InvoiceActions({ iv, keapOrder }: { iv: FixableInvoice; keapOrder?: boolean }) {
  const [open, setOpen] = useState<"void" | "manual" | null>(null);
  const owed = iv.amount_due_cents - iv.amount_paid_cents;
  if (keapOrder) return null; // Keap orders finish in Keap
  const canVoid = ["open", "draft", "awaiting_approval"].includes(iv.status) && iv.amount_paid_cents === 0;
  const canRecord = iv.status === "open" && owed > 0;
  if (!canVoid && !canRecord) return null;
  return (
    <span className="flex flex-wrap justify-end gap-1">
      {canRecord && (
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setOpen("manual")}>
          Record a payment
        </Button>
      )}
      {canVoid && (
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setOpen("void")}>
          Void
        </Button>
      )}
      {open === "void" && <VoidInvoiceDialog iv={iv} onClose={() => setOpen(null)} />}
      {open === "manual" && <ManualPaymentDialog iv={iv} onClose={() => setOpen(null)} />}
    </span>
  );
}

/** invoices_void: the invoice is never charged; its payment-plan date is cancelled. */
export function VoidInvoiceDialog({ iv, onClose }: { iv: FixableInvoice; onClose: () => void }) {
  const refresh = useRefresh();
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [problem, setProblem] = useState("");
  const run = useMutation({
    mutationFn: () => api(`/api/v1/invoices/${iv.id}/void`, { json: { reason: reason.trim() } }),
    onMutate: () => setProblem(""),
    onSuccess: () => {
      refresh();
      toast(`Invoice ${iv.number} voided`);
      onClose();
    },
    onError: (e) => setProblem(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(500px,calc(100vw-2rem))]">
        <DialogTitle>Void invoice {iv.number}?</DialogTitle>
        <DialogDescription>
          {formatCents(iv.amount_due_cents - iv.amount_paid_cents)} will never be charged and its pay link stops working. No money moves and the customer isn't emailed.
          {iv.subscription_id ? " This is a subscription invoice: that period goes unpaid, and a member who was behind only because of it is back in good standing." : ""}
        </DialogDescription>
        <div className="space-y-1 text-sm">
          <Label htmlFor="vi-reason">Reason (required)</Label>
          <Input id="vi-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Paid in Keap / made by mistake" />
        </div>
        <Problem text={problem} />
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Keep it
          </Button>
          <Button variant="destructive" disabled={!reason.trim() || run.isPending} onClick={() => run.mutate()}>
            Void {iv.number}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const METHODS: [string, string][] = [
  ["check", "Check"],
  ["wire", "Wire or bank transfer"],
  ["cash", "Cash"],
  ["other", "Other"],
];

/** payments_record_manual: money that came in outside the card gateways. Review step, then record. */
export function ManualPaymentDialog({ iv, onClose }: { iv: FixableInvoice; onClose: () => void }) {
  const refresh = useRefresh();
  const toast = useToast();
  const owed = iv.amount_due_cents - iv.amount_paid_cents;
  const [method, setMethod] = useState("check");
  const [amount, setAmount] = useState(centsToInput(owed));
  const [reference, setReference] = useState("");
  const [receivedOn, setReceivedOn] = useState(mountainDay());
  const [notify, setNotify] = useState(false);
  const [review, setReview] = useState(false);
  const [problem, setProblem] = useState("");
  const cents = toCents(amount);
  const amountOk = cents !== null && cents > 0 && cents <= owed;
  const dateOk = !!receivedOn && receivedOn <= mountainDay();
  const run = useMutation({
    mutationFn: () =>
      api(`/api/v1/invoices/${iv.id}/manual-payment`, {
        json: { method, amount_cents: cents === owed ? undefined : cents, reference: reference.trim() || undefined, received_on: receivedOn, notify: notify || undefined },
      }),
    onMutate: () => setProblem(""),
    onSuccess: () => {
      refresh();
      toast(`Recorded ${formatCents(cents)} on ${iv.number}`);
      onClose();
    },
    onError: (e) => setProblem(e.message),
  });
  const methodName = METHODS.find(([k]) => k === method)?.[1] ?? method;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(540px,calc(100vw-2rem))]">
        <DialogTitle>{review ? "Check before recording" : `Record a payment on ${iv.number}`}</DialogTitle>
        {!review ? (
          <>
            <DialogDescription>
              For money that came in outside the card forms. Nothing is charged; the order, plan and access update as if it was paid by card. {formatCents(owed)} is owed.
            </DialogDescription>
            <div className="space-y-3 text-sm">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="mp-method">How it was paid</Label>
                  <Select id="mp-method" value={method} onChange={(e) => setMethod(e.target.value)}>
                    {METHODS.map(([k, l]) => (
                      <option key={k} value={k}>
                        {l}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="mp-amount">Amount ($)</Label>
                  <Input id="mp-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
                  {amount && !amountOk && <p className="text-xs text-[var(--color-danger-fg)]">More than $0 and up to {formatCents(owed)}.</p>}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="mp-ref">Check number or reference</Label>
                  <Input id="mp-ref" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Check 1042" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="mp-date">Received on</Label>
                  <Input id="mp-date" type="date" max={mountainDay()} value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} />
                </div>
              </div>
              <label className="flex items-start gap-2">
                <Checkbox className="mt-0.5" checked={notify} onChange={(e) => setNotify(e.target.checked)} /> Email the payer a receipt (off by default: Keap never emailed for these)
              </label>
            </div>
          </>
        ) : (
          <Facts
            rows={[
              ["Invoice", iv.number],
              ["Amount", `${formatCents(cents)}${cents === owed ? " (all that is owed)" : ` (part; ${formatCents(owed - (cents ?? 0))} still owed)`}`],
              ["Paid by", methodName],
              ["Reference", reference.trim() || "none"],
              ["Received", formatDate(receivedOn)],
              ["Receipt email", notify ? "yes, to the payer" : "no"],
            ]}
          />
        )}
        <Problem text={problem} />
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={review ? () => setReview(false) : onClose}>
            {review ? "Back" : "Cancel"}
          </Button>
          {!review ? (
            <Button disabled={!amountOk || !dateOk} onClick={() => setReview(true)}>
              Review
            </Button>
          ) : (
            <Button disabled={run.isPending} onClick={() => run.mutate()}>
              Record {formatCents(cents)}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export interface Unresolved {
  kind: "payment" | "refund";
  id: number;
  amount_cents: number;
  gateway?: string | null;
}

/** "Record what the gateway shows" on a payment or refund whose answer was lost (status pending or unknown). Admins only. */
export function ResolveButton({ item }: { item: Unresolved }) {
  const isAdmin = useIsAdmin();
  const [open, setOpen] = useState(false);
  if (!isAdmin) return null;
  return (
    <>
      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setOpen(true)}>
        Record what {gatewayName(item.gateway ?? "") || "the gateway"} shows
      </Button>
      {open && <ResolveDialog item={item} onClose={() => setOpen(false)} />}
    </>
  );
}

/** billing_payment_resolve (money op: preview, then confirm). Moves no money itself. */
export function ResolveDialog({ item, onClose }: { item: Unresolved; onClose: () => void }) {
  const refresh = useRefresh();
  const toast = useToast();
  const [outcome, setOutcome] = useState<"done" | "not_done" | "">("");
  const [txn, setTxn] = useState("");
  const [note, setNote] = useState("");
  const gw = gatewayName(item.gateway ?? "") || "the gateway";
  const flow = usePreviewConfirm<{ what_happens?: string }, { status: string; message: string }>(
    (confirm) =>
      api(`/api/v1/billing/resolve`, {
        json: {
          [item.kind === "payment" ? "payment_id" : "refund_id"]: item.id,
          outcome,
          gateway_txn_id: outcome === "done" ? txn.trim() : undefined,
          note: note.trim() || undefined,
          confirm: confirm || undefined,
        },
      }),
    (r) => {
      refresh();
      toast(r.message || "Recorded");
      onClose();
    },
  );
  const ready = outcome === "not_done" || (outcome === "done" && txn.trim() !== "");
  const what = item.kind === "payment" ? "charge" : "refund";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(560px,calc(100vw-2rem))]">
        <DialogTitle>
          {flow.preview ? "Check before recording" : `What does ${gw} show for this ${formatCents(item.amount_cents)} ${what}?`}
        </DialogTitle>
        {!flow.preview ? (
          <>
            <DialogDescription>
              The CRM lost {gw}'s answer for {item.kind} #{item.id}, so it doesn't know whether the money moved. Look it up in {gw}'s dashboard first, then record what it shows.
              This moves no money.
            </DialogDescription>
            <div className="space-y-3 text-sm">
              <fieldset className="space-y-2">
                <legend className="sr-only">What the gateway shows</legend>
                <label className="flex items-start gap-2">
                  <input type="radio" name="rs-outcome" className="mt-1" checked={outcome === "done"} onChange={() => setOutcome("done")} />
                  <span>It went through ({gw} shows the {what})</span>
                </label>
                <label className="flex items-start gap-2">
                  <input type="radio" name="rs-outcome" className="mt-1" checked={outcome === "not_done"} onChange={() => setOutcome("not_done")} />
                  <span>
                    It didn't happen ({gw} has no such {what}).{" "}
                    <span className="text-[var(--color-muted-foreground)]">
                      {item.kind === "payment" ? "The invoice opens again so it can be charged again." : "The payment can then be refunded again."}
                    </span>
                  </span>
                </label>
              </fieldset>
              {outcome === "done" && (
                <div className="space-y-1">
                  <Label htmlFor="rs-txn">{gw}'s transaction ID</Label>
                  <Input id="rs-txn" value={txn} onChange={(e) => setTxn(e.target.value)} placeholder="ch_... / pi_... / 60123456789" />
                </div>
              )}
              <div className="space-y-1">
                <Label htmlFor="rs-note">Note (optional)</Label>
                <Input id="rs-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Checked the Stripe dashboard at 2:10 PM" />
              </div>
            </div>
          </>
        ) : (
          <div className="space-y-2">
            <ServerSays text={plainConfirmMessage(flow.preview.message)} />
            {flow.preview.details.what_happens && <p className="text-sm">{flow.preview.details.what_happens}</p>}
          </div>
        )}
        <Problem text={flow.problem} />
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={flow.preview ? flow.back : onClose}>
            {flow.preview ? "Back" : "Cancel"}
          </Button>
          {!flow.preview ? (
            <Button disabled={!ready || flow.reviewing} onClick={flow.review}>
              Review
            </Button>
          ) : (
            <Button variant="destructive" disabled={flow.confirming} onClick={flow.confirm}>
              Record it
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
